import React, { useEffect, useState, useMemo } from "react";
import PropTypes from "prop-types";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Box,
  Typography,
  Grid,
  Paper,
  Tabs,
  Tab,
  CircularProgress,
  Autocomplete,
  TextField,
  Button,
  Chip,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import GroupWorkIcon from "@mui/icons-material/GroupWork";
import EventIcon from "@mui/icons-material/Event";
import PeopleIcon from "@mui/icons-material/People";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import FilterListIcon from "@mui/icons-material/FilterList";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDBadge from "components/MDBadge";
import DataTable from "examples/Tables/DataTable";
import ReportsBarChart from "examples/Charts/BarCharts/ReportsBarChart";
import ReportsLineChart from "examples/Charts/LineCharts/ReportsLineChart";
import axiosInstance from "API/axiosInstance";
import { API_ENDPOINTS } from "config/api";
import safeToast from "utils/safeToast";

function TabPanel({ children, value, index, ...other }) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`analytics-tabpanel-${index}`}
      aria-labelledby={`analytics-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

TabPanel.propTypes = {
  children: PropTypes.node,
  index: PropTypes.number.isRequired,
  value: PropTypes.number.isRequired,
};

const AdminChapterEventAnalyticsDialog = ({ open, onClose }) => {
  const [tabValue, setTabValue] = useState(0);
  const [loading, setLoading] = useState(false);
  const [chapterAnalytics, setChapterAnalytics] = useState(null);
  const [eventAnalytics, setEventAnalytics] = useState(null);

  // Filter states for Chapters
  const [chapterStatusFilter, setChapterStatusFilter] = useState("all");
  const [chapterLocationFilter, setChapterLocationFilter] = useState(null);

  // Filter states for Events
  const [eventChapterFilter, setEventChapterFilter] = useState(null);
  const [eventDateFilter, setEventDateFilter] = useState("all");

  // Get unique filter options
  const chapterLocations = useMemo(() => {
    if (!chapterAnalytics?.chapters) return [];
    const locations = chapterAnalytics.chapters
      .map((c) => c.location)
      .filter((loc) => loc && loc.trim() !== "");
    return [...new Set(locations)].sort();
  }, [chapterAnalytics]);

  const eventChapters = useMemo(() => {
    if (!eventAnalytics?.events) return [];
    const chapters = eventAnalytics.events.map((e) => e.chapterName);
    return [...new Set(chapters)].sort();
  }, [eventAnalytics]);

  // Filter and prepare Event Table Data
  const eventTableData = useMemo(() => {
    if (!eventAnalytics?.events) {
      return { columns: [], rows: [] };
    }

    // Apply filters
    let filteredEvents = [...eventAnalytics.events];

    if (eventChapterFilter) {
      filteredEvents = filteredEvents.filter((e) => e.chapterName === eventChapterFilter);
    }

    if (eventDateFilter === "upcoming") {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      filteredEvents = filteredEvents.filter((e) => new Date(e.eventDate) >= today);
    } else if (eventDateFilter === "past") {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      filteredEvents = filteredEvents.filter((e) => new Date(e.eventDate) < today);
    }

    const columns = [
      { Header: "Event Name", accessor: "eventName", width: "25%", align: "left" },
      { Header: "Chapter", accessor: "chapterName", width: "20%", align: "left" },
      { Header: "Event Date", accessor: "eventDate", width: "15%", align: "left" },
      { Header: "Venue", accessor: "venue", width: "20%", align: "left" },
      { Header: "Total Members", accessor: "totalMembers", width: "10%", align: "right" },
      { Header: "Active Members", accessor: "activeMembers", width: "10%", align: "right" },
    ];

    const rows = filteredEvents.map((event) => ({
      eventName: (
        <MDTypography variant="caption" fontWeight="medium">
          {event.eventName}
        </MDTypography>
      ),
      chapterName: (
        <MDTypography variant="caption" color="text">
          {event.chapterName}
        </MDTypography>
      ),
      eventDate: (
        <MDTypography variant="caption" color="text">
          {new Date(event.eventDate).toLocaleDateString("en-US", {
            year: "numeric",
            month: "short",
            day: "numeric",
          })}
        </MDTypography>
      ),
      venue: (
        <MDTypography variant="caption" color="text">
          {event.venue || "N/A"}
        </MDTypography>
      ),
      totalMembers: (
        <MDTypography variant="caption" fontWeight="medium" color="text">
          {event.totalMembers.toLocaleString()}
        </MDTypography>
      ),
      activeMembers: (
        <MDTypography variant="caption" fontWeight="medium" color="success">
          {event.activeMembers.toLocaleString()}
        </MDTypography>
      ),
    }));

    return { columns, rows, filteredEvents };
  }, [eventAnalytics, eventChapterFilter, eventDateFilter]);

  // Filter and prepare Chapter Table Data
  const chapterTableData = useMemo(() => {
    if (!chapterAnalytics?.chapters) {
      return { columns: [], rows: [] };
    }

    // Apply filters
    let filteredChapters = [...chapterAnalytics.chapters];

    if (chapterStatusFilter === "active") {
      filteredChapters = filteredChapters.filter((c) => c.isActive);
    } else if (chapterStatusFilter === "inactive") {
      filteredChapters = filteredChapters.filter((c) => !c.isActive);
    }

    if (chapterLocationFilter) {
      filteredChapters = filteredChapters.filter((c) => c.location === chapterLocationFilter);
    }

    const columns = [
      { Header: "Chapter Name", accessor: "chapterName", width: "25%", align: "left" },
      { Header: "Location", accessor: "location", width: "20%", align: "left" },
      { Header: "Total Members", accessor: "totalMembers", width: "15%", align: "right" },
      { Header: "Active Members", accessor: "activeMembers", width: "15%", align: "right" },
      { Header: "Total Events", accessor: "totalEvents", width: "10%", align: "right" },
      { Header: "Status", accessor: "status", width: "15%", align: "center" },
    ];

    const rows = filteredChapters.map((chapter) => ({
      chapterName: (
        <MDTypography variant="caption" fontWeight="medium">
          {chapter.chapterName}
        </MDTypography>
      ),
      location: (
        <MDTypography variant="caption" color="text">
          {chapter.location || "N/A"}
        </MDTypography>
      ),
      totalMembers: (
        <MDTypography variant="caption" fontWeight="medium" color="text">
          {chapter.totalMembers.toLocaleString()}
        </MDTypography>
      ),
      activeMembers: (
        <MDTypography variant="caption" fontWeight="medium" color="success">
          {chapter.activeMembers.toLocaleString()}
        </MDTypography>
      ),
      totalEvents: (
        <MDTypography variant="caption" fontWeight="medium" color="text">
          {chapter.totalEvents.toLocaleString()}
        </MDTypography>
      ),
      status: (
        <MDBox ml={-1}>
          <MDBadge
            badgeContent={chapter.isActive ? "Active" : "Inactive"}
            color={chapter.isActive ? "success" : "error"}
            variant="gradient"
            size="sm"
          />
        </MDBox>
      ),
    }));

    return { columns, rows, filteredChapters };
  }, [chapterAnalytics, chapterStatusFilter, chapterLocationFilter]);

  // CSV Export Functions
  const exportChaptersToCSV = () => {
    if (!chapterTableData.filteredChapters || chapterTableData.filteredChapters.length === 0) {
      safeToast.warn("No data to export");
      return;
    }

    try {
      const headers = [
        "Chapter Name",
        "Location",
        "Total Members",
        "Active Members",
        "Total Events",
        "Status",
      ];
      const csvContent = [
        headers.join(","),
        ...chapterTableData.filteredChapters.map((chapter) => [
          `"${(chapter.chapterName || "").replace(/"/g, '""')}"`,
          `"${(chapter.location || "N/A").replace(/"/g, '""')}"`,
          chapter.totalMembers,
          chapter.activeMembers,
          chapter.totalEvents,
          `"${chapter.isActive ? "Active" : "Inactive"}"`,
        ].join(",")),
      ].join("\n");

      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const link = document.createElement("a");
      const url = URL.createObjectURL(blob);
      link.setAttribute("href", url);
      link.setAttribute(
        "download",
        `chapters-${new Date().toISOString().slice(0, 10)}.csv`
      );
      link.style.visibility = "hidden";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      safeToast.success("Chapters data exported successfully");
    } catch (error) {
      console.error("CSV export error:", error);
      safeToast.error("Failed to export CSV. Please try again.");
    }
  };

  const exportEventsToCSV = () => {
    if (!eventTableData.filteredEvents || eventTableData.filteredEvents.length === 0) {
      safeToast.warn("No data to export");
      return;
    }

    try {
      const headers = [
        "Event Name",
        "Chapter",
        "Event Date",
        "Venue",
        "Total Members",
        "Active Members",
      ];
      const csvContent = [
        headers.join(","),
        ...eventTableData.filteredEvents.map((event) => [
          `"${(event.eventName || "").replace(/"/g, '""')}"`,
          `"${(event.chapterName || "").replace(/"/g, '""')}"`,
          `"${new Date(event.eventDate).toLocaleDateString("en-US", {
            year: "numeric",
            month: "short",
            day: "numeric",
          })}"`,
          `"${(event.venue || "N/A").replace(/"/g, '""')}"`,
          event.totalMembers,
          event.activeMembers,
        ].join(",")),
      ].join("\n");

      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const link = document.createElement("a");
      const url = URL.createObjectURL(blob);
      link.setAttribute("href", url);
      link.setAttribute(
        "download",
        `events-${new Date().toISOString().slice(0, 10)}.csv`
      );
      link.style.visibility = "hidden";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      safeToast.success("Events data exported successfully");
    } catch (error) {
      console.error("CSV export error:", error);
      safeToast.error("Failed to export CSV. Please try again.");
    }
  };

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    
    // Fetch both analytics in parallel
    Promise.all([
      axiosInstance.get(API_ENDPOINTS.ADMIN.GET_CHAPTER_ANALYTICS),
      axiosInstance.get(API_ENDPOINTS.ADMIN.GET_EVENT_ANALYTICS),
    ])
      .then(([chapterRes, eventRes]) => {
        setChapterAnalytics(chapterRes.data);
        setEventAnalytics(eventRes.data);
      })
      .catch((error) => {
        console.error("Error fetching analytics:", error);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [open]);

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  // Prepare chart data for chapters
  const chapterMembersChart = chapterAnalytics
    ? {
        labels: chapterAnalytics.membersByChapter?.labels || [],
        datasets: {
          label: "Active Members",
          data: chapterAnalytics.membersByChapter?.values || [],
        },
      }
    : { labels: [], datasets: { label: "", data: [] } };

  const chapterEventsChart = chapterAnalytics
    ? {
        labels: chapterAnalytics.eventsByChapter?.labels || [],
        datasets: {
          label: "Events",
          data: chapterAnalytics.eventsByChapter?.values || [],
        },
      }
    : { labels: [], datasets: { label: "", data: [] } };

  const membersOverTimeChart = chapterAnalytics
    ? {
        labels: chapterAnalytics.membersOverTime?.labels || [],
        datasets: {
          label: "New Members",
          data: chapterAnalytics.membersOverTime?.values || [],
        },
      }
    : { labels: [], datasets: { label: "", data: [] } };

  // Prepare chart data for events
  const eventMembersChart = eventAnalytics
    ? {
        labels: eventAnalytics.membersByEvent?.labels || [],
        datasets: {
          label: "Active Members",
          data: eventAnalytics.membersByEvent?.values || [],
        },
      }
    : { labels: [], datasets: { label: "", data: [] } };

  const eventsByChapterChart = eventAnalytics
    ? {
        labels: eventAnalytics.eventsByChapter?.labels || [],
        datasets: {
          label: "Events",
          data: eventAnalytics.eventsByChapter?.values || [],
        },
      }
    : { labels: [], datasets: { label: "", data: [] } };

  const eventsOverTimeChart = eventAnalytics
    ? {
        labels: eventAnalytics.eventsOverTime?.labels || [],
        datasets: {
          label: "Events",
          data: eventAnalytics.eventsOverTime?.values || [],
        },
      }
    : { labels: [], datasets: { label: "", data: [] } };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xl" fullScreen>
      <Box sx={{ position: "absolute", top: 8, right: 8, zIndex: 2 }}>
        <IconButton onClick={onClose} color="error">
          <CloseIcon />
        </IconButton>
      </Box>

      <DialogTitle>
        <Box display="flex" alignItems="center" gap={2}>
          <GroupWorkIcon color="primary" />
          <MDTypography variant="h4" fontWeight="bold">
            Chapter & Event Analytics
          </MDTypography>
        </Box>
      </DialogTitle>

      <DialogContent>
        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
            <CircularProgress />
          </Box>
        ) : (
          <>
            <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 2 }}>
              <Tabs value={tabValue} onChange={handleTabChange}>
                <Tab label="Chapter Analytics" icon={<GroupWorkIcon />} iconPosition="start" />
                <Tab label="Event Analytics" icon={<EventIcon />} iconPosition="start" />
              </Tabs>
            </Box>

            {/* Chapter Analytics Tab */}
            <TabPanel value={tabValue} index={0}>
              <Grid container spacing={3}>
                {/* Charts Row 1 */}
                <Grid item xs={12} md={6}>
                  <MDBox mb={3}>
                    <ReportsBarChart
                      color="info"
                      title="Members by Chapter"
                      description="Active members per chapter"
                      date="Current data"
                      chart={chapterMembersChart}
                    />
                  </MDBox>
                </Grid>
                <Grid item xs={12} md={6}>
                  <MDBox mb={3}>
                    <ReportsBarChart
                      color="success"
                      title="Events by Chapter"
                      description="Total events per chapter"
                      date="Current data"
                      chart={chapterEventsChart}
                    />
                  </MDBox>
                </Grid>

                {/* Chart Row 2 */}
                <Grid item xs={12}>
                  <MDBox mb={3}>
                    <ReportsLineChart
                      color="primary"
                      title="Members Growth Over Time"
                      description="New members joining chapters monthly"
                      date="Historical data"
                      chart={membersOverTimeChart}
                    />
                  </MDBox>
                </Grid>

                {/* Chapters Table */}
                <Grid item xs={12}>
                  <Paper elevation={2} sx={{ p: { xs: 1, sm: 2 }, overflow: "hidden" }}>
                    <MDBox
                      mx={2}
                      mt={-3}
                      py={3}
                      px={2}
                      variant="gradient"
                      bgColor="info"
                      borderRadius="lg"
                      coloredShadow="info"
                      display="flex"
                      justifyContent="space-between"
                      alignItems="center"
                    >
                      <MDTypography variant="h6" color="white">
                        Chapter Details
                      </MDTypography>
                      <MDButton
                        variant="outlined"
                        color="white"
                        size="small"
                        onClick={exportChaptersToCSV}
                        startIcon={<FileDownloadIcon />}
                      >
                        Export CSV
                      </MDButton>
                    </MDBox>

                    {/* Filter Controls */}
                    <MDBox p={2} sx={{ borderBottom: 1, borderColor: "divider" }}>
                      <Box
                        sx={{
                          display: "flex",
                          gap: 2,
                          alignItems: "center",
                          flexWrap: "wrap",
                        }}
                      >
                        <FilterListIcon color="action" />
                        <Autocomplete
                          size="small"
                          sx={{ minWidth: 150 }}
                          options={[
                            { value: "all", label: "All Status" },
                            { value: "active", label: "Active" },
                            { value: "inactive", label: "Inactive" },
                          ]}
                          value={
                            [
                              { value: "all", label: "All Status" },
                              { value: "active", label: "Active" },
                              { value: "inactive", label: "Inactive" },
                            ].find((opt) => opt.value === chapterStatusFilter) ||
                            { value: "all", label: "All Status" }
                          }
                          onChange={(event, newValue) => {
                            setChapterStatusFilter(newValue?.value || "all");
                          }}
                          getOptionLabel={(option) => option.label}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label="Status"
                              sx={{
                                "& .MuiInputLabel-root": { fontSize: "0.8rem" },
                                "& .MuiInputBase-input": { fontSize: "0.8rem" },
                              }}
                            />
                          )}
                        />

                        <Autocomplete
                          size="small"
                          sx={{ minWidth: 180 }}
                          options={chapterLocations}
                          value={chapterLocationFilter}
                          onChange={(event, newValue) => {
                            setChapterLocationFilter(newValue);
                          }}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label="Location"
                              sx={{
                                "& .MuiInputLabel-root": { fontSize: "0.8rem" },
                                "& .MuiInputBase-input": { fontSize: "0.8rem" },
                              }}
                            />
                          )}
                        />

                        {(chapterStatusFilter !== "all" || chapterLocationFilter) && (
                          <Chip
                            label="Clear Filters"
                            size="small"
                            onDelete={() => {
                              setChapterStatusFilter("all");
                              setChapterLocationFilter(null);
                            }}
                            deleteIcon={<CloseIcon />}
                            color="error"
                            variant="outlined"
                          />
                        )}

                        <MDTypography variant="caption" color="text.secondary" sx={{ ml: "auto" }}>
                          {chapterTableData.rows.length} of {chapterAnalytics?.chapters?.length || 0}{" "}
                          chapters
                        </MDTypography>
                      </Box>
                    </MDBox>

                    <MDBox pt={3}>
                      {chapterTableData.rows.length > 0 ? (
                        <DataTable
                          table={chapterTableData}
                          isSorted={true}
                          entriesPerPage={{ defaultValue: 10, entries: [5, 10, 15, 20, 25] }}
                          canSearch={true}
                          showTotalEntries={true}
                          pagination={{ variant: "gradient", color: "info" }}
                          noEndBorder
                        />
                      ) : (
                        <MDBox p={4} textAlign="center">
                          <MDTypography variant="body2" color="text.secondary">
                            No chapters found
                          </MDTypography>
                        </MDBox>
                      )}
                    </MDBox>
                  </Paper>
                </Grid>
              </Grid>
            </TabPanel>

            {/* Event Analytics Tab */}
            <TabPanel value={tabValue} index={1}>
              <Grid container spacing={3}>
                {/* Charts Row 1 */}
                <Grid item xs={12} md={6}>
                  <MDBox mb={3}>
                    <ReportsBarChart
                      color="warning"
                      title="Members by Event"
                      description="Active members per event"
                      date="Current data"
                      chart={eventMembersChart}
                    />
                  </MDBox>
                </Grid>
                <Grid item xs={12} md={6}>
                  <MDBox mb={3}>
                    <ReportsBarChart
                      color="error"
                      title="Events by Chapter"
                      description="Total events per chapter"
                      date="Current data"
                      chart={eventsByChapterChart}
                    />
                  </MDBox>
                </Grid>

                {/* Chart Row 2 */}
                <Grid item xs={12}>
                  <MDBox mb={3}>
                    <ReportsLineChart
                      color="secondary"
                      title="Events Over Time"
                      description="Events created monthly"
                      date="Historical data"
                      chart={eventsOverTimeChart}
                    />
                  </MDBox>
                </Grid>

                {/* Events Table */}
                <Grid item xs={12}>
                  <Paper elevation={2} sx={{ p: { xs: 1, sm: 2 }, overflow: "hidden" }}>
                    <MDBox
                      mx={2}
                      mt={-3}
                      py={3}
                      px={2}
                      variant="gradient"
                      bgColor="warning"
                      borderRadius="lg"
                      coloredShadow="warning"
                      display="flex"
                      justifyContent="space-between"
                      alignItems="center"
                    >
                      <MDTypography variant="h6" color="white">
                        Event Details
                      </MDTypography>
                      <MDButton
                        variant="outlined"
                        color="white"
                        size="small"
                        onClick={exportEventsToCSV}
                        startIcon={<FileDownloadIcon />}
                      >
                        Export CSV
                      </MDButton>
                    </MDBox>

                    {/* Filter Controls */}
                    <MDBox p={2} sx={{ borderBottom: 1, borderColor: "divider" }}>
                      <Box
                        sx={{
                          display: "flex",
                          gap: 2,
                          alignItems: "center",
                          flexWrap: "wrap",
                        }}
                      >
                        <FilterListIcon color="action" />
                        <Autocomplete
                          size="small"
                          sx={{ minWidth: 180 }}
                          options={eventChapters}
                          value={eventChapterFilter}
                          onChange={(event, newValue) => {
                            setEventChapterFilter(newValue);
                          }}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label="Filter by Chapter"
                              sx={{
                                "& .MuiInputLabel-root": { fontSize: "0.8rem" },
                                "& .MuiInputBase-input": { fontSize: "0.8rem" },
                              }}
                            />
                          )}
                        />

                        <Autocomplete
                          size="small"
                          sx={{ minWidth: 150 }}
                          options={[
                            { value: "all", label: "All Dates" },
                            { value: "upcoming", label: "Upcoming" },
                            { value: "past", label: "Past" },
                          ]}
                          value={
                            [
                              { value: "all", label: "All Dates" },
                              { value: "upcoming", label: "Upcoming" },
                              { value: "past", label: "Past" },
                            ].find((opt) => opt.value === eventDateFilter) ||
                            { value: "all", label: "All Dates" }
                          }
                          onChange={(event, newValue) => {
                            setEventDateFilter(newValue?.value || "all");
                          }}
                          getOptionLabel={(option) => option.label}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label="Date Filter"
                              sx={{
                                "& .MuiInputLabel-root": { fontSize: "0.8rem" },
                                "& .MuiInputBase-input": { fontSize: "0.8rem" },
                              }}
                            />
                          )}
                        />

                        {(eventChapterFilter || eventDateFilter !== "all") && (
                          <Chip
                            label="Clear Filters"
                            size="small"
                            onDelete={() => {
                              setEventChapterFilter(null);
                              setEventDateFilter("all");
                            }}
                            deleteIcon={<CloseIcon />}
                            color="error"
                            variant="outlined"
                          />
                        )}

                        <MDTypography variant="caption" color="text.secondary" sx={{ ml: "auto" }}>
                          {eventTableData.rows.length} of {eventAnalytics?.events?.length || 0}{" "}
                          events
                        </MDTypography>
                      </Box>
                    </MDBox>

                    <MDBox pt={3}>
                      {eventTableData.rows.length > 0 ? (
                        <DataTable
                          table={eventTableData}
                          isSorted={true}
                          entriesPerPage={{ defaultValue: 10, entries: [5, 10, 15, 20, 25] }}
                          canSearch={true}
                          showTotalEntries={true}
                          pagination={{ variant: "gradient", color: "warning" }}
                          noEndBorder
                        />
                      ) : (
                        <MDBox p={4} textAlign="center">
                          <MDTypography variant="body2" color="text.secondary">
                            No events found
                          </MDTypography>
                        </MDBox>
                      )}
                    </MDBox>
                  </Paper>
                </Grid>
              </Grid>
            </TabPanel>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

AdminChapterEventAnalyticsDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default AdminChapterEventAnalyticsDialog;

